import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbBriefcase, TbChevronDown, TbMapPin } from 'react-icons/tb';
import '../i18n';
import { clientLogos, experiences, formatMonth } from '../data/profile';
import Reveal from './Reveal';
import './assets/css/experiences.css';

const VISIBLE_BULLETS = 5;

function ExperienceItem({ exp, index }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bullets = t(exp.bullets, { returnObjects: true });
  const list = Array.isArray(bullets) ? bullets : [];
  const canExpand = list.length > VISIBLE_BULLETS + 1;
  const shown = canExpand && !expanded ? list.slice(0, VISIBLE_BULLETS) : list;
  const lang = i18n.language;
  const isCurrent = exp.end === null;

  return (
    <Reveal as="li" className={`timeline-item ${isCurrent ? 'current' : ''}`} delay={index * 0.05}>
      <span className="timeline-dot" aria-hidden="true" />
      <article className="exp-card">
        <header className="exp-head">
          <div>
            <h3 className="exp-role">{t(exp.role)}</h3>
            <p className="exp-company">
              <TbBriefcase aria-hidden="true" /> {exp.company}
              <span className="sep">·</span>
              <TbMapPin aria-hidden="true" /> {exp.location === 'remote' ? t('remote') : exp.location}
            </p>
          </div>
          <span className={`exp-period ${isCurrent ? 'is-current' : ''}`}>
            {formatMonth(exp.start, lang)} — {isCurrent ? t('present') : formatMonth(exp.end, lang)}
          </span>
        </header>

        <ul className="exp-bullets">
          {shown.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>

        {canExpand && (
          <button
            className={`exp-more ${expanded ? 'open' : ''}`}
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          >
            {expanded ? t('show_less') : t('show_more', { count: list.length - VISIBLE_BULLETS })}
            <TbChevronDown aria-hidden="true" />
          </button>
        )}

        <ul className="exp-tech">
          {exp.tech.map((tech) => (
            <li key={tech} className="tag">{tech}</li>
          ))}
        </ul>
      </article>
    </Reveal>
  );
}

function Experiences() {
  const { t } = useTranslation();

  return (
    <section className="section" id="experience">
      <div className="section-inner">
        <Reveal className="section-header">
          <span className="section-eyebrow">{t('exp_eyebrow')}</span>
          <h2 className="section-title">{t('exp_title')}</h2>
        </Reveal>

        <ol className="timeline">
          {experiences.map((exp, index) => (
            <ExperienceItem key={exp.role} exp={exp} index={index} />
          ))}
        </ol>

        <Reveal className="clients">
          <h3 className="clients-title">{t('clients_title')}</h3>
          <p className="clients-subtitle">{t('clients_subtitle')}</p>
          <div className="marquee">
            <ul className="marquee-track">
              {/* Lista duplicada para que el desplazamiento sea continuo */}
              {[...clientLogos, ...clientLogos].map((img, i) => (
                <li key={i} className="logo-tile" aria-hidden={i >= clientLogos.length}>
                  <img
                    src={`/assets/images/${img}`}
                    alt={img.replace(/\d*\.(png|jpe?g)$/, '').replace('logo_', '')}
                    loading="lazy"
                  />
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default Experiences;
